// Web implementation of EnrichedTextInput. Renders a single
// `<div contenteditable>` and drives it through the command + paste
// + markdown modules. Exposes the same imperative ref handle the
// macOS module (phase 2) will implement against `NSTextView`.

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  EnrichedEditorState,
  EnrichedTextInputProps,
  EnrichedTextInputRef,
  EnrichedTriggerContext,
} from './types';
import { markdownToHtml } from './markdown/parser';
import { htmlToMarkdown } from './markdown/serializer';
import { detectLanguage } from './markdown/detectLanguage';
import {
  handleBackspaceInCodeBlock,
  handleBackspaceInList,
  handleEnterInList,
  indentListItem,
  isCaretOnLastLineOfCode,
  outdentListItem,
  toggleBlockType,
  toggleCodeBlock as toggleCodeBlockCmd,
  toggleListBlock,
  tryConvertParagraphToCodeBlock,
  tryEscalateCodeFenceOnType,
  tryExitCodeBlockBelow,
  tryExitCodeFence,
  tryOpenCodeBlockOnType,
} from './commands/blocks';
import { CODE_LANG_CHIP_ATTR, ensureCodeChrome } from './commands/codeChrome';
import {
  EditorHistory,
  restoreCaretOffset,
  snapshotEditor,
} from './commands/history';
import {
  findAncestorAnchor,
  setLink as setLinkCmd,
  toggleInlineMark,
  unwrapLink as unwrapLinkCmd,
} from './commands/inline';
import {
  autodetectCodeBlocks,
  autolinkInPlace,
  buildCodeBlockFragment,
  classifyClipboard,
  htmlStructuralLineCount,
  looksLikeCode,
  normalizeUrl,
  plainTextToFragment,
  stripMarkdownFormatting,
} from './commands/paste';
import { applyEmoticon, applyInlineAutoFormat } from './commands/autoFormat';
import { normalizeBlocks } from './commands/normalize';
import { deriveActiveFormats } from './commands/activeFormats';
import {
  commitEmoji,
  commitMention,
  detectTrigger,
  type TriggerWindow,
} from './commands/triggers';
import { ensureEditorStyles } from './styles';

/**
 * Resolve a key event into a logical action. Mirrors the binding
 * table used by the legacy textarea-based editor so the same
 * shortcuts (Cmd+B, Cmd+I, Cmd+Shift+X, ...) keep working. We add
 * Cmd+K (link, Slack-style) on top of the original Cmd+Shift+U.
 */
function resolveShortcut(e: KeyboardEvent): null | {
  readonly kind: 'inline';
  readonly tag: 'STRONG' | 'EM' | 'S' | 'CODE';
} | {
  readonly kind: 'block';
  readonly target: 'BLOCKQUOTE';
} | {
  readonly kind: 'list';
  readonly tag: 'UL' | 'OL';
} | {
  readonly kind: 'codeBlock';
} | {
  readonly kind: 'link';
} | {
  readonly kind: 'applyMarkdown';
} {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return null;
  const key = e.key.toLowerCase();
  const shift = e.shiftKey;
  const alt = e.altKey;
  if (key === 'b' && !shift && !alt) return { kind: 'inline', tag: 'STRONG' };
  if (key === 'i' && !shift && !alt) return { kind: 'inline', tag: 'EM' };
  if (key === 'x' && shift && !alt) return { kind: 'inline', tag: 'S' };
  if (key === 'c' && shift && !alt) return { kind: 'inline', tag: 'CODE' };
  if (key === 'c' && shift && alt) return { kind: 'codeBlock' };
  if (key === '7' && shift && !alt) return { kind: 'list', tag: 'OL' };
  if (key === '8' && shift && !alt) return { kind: 'list', tag: 'UL' };
  if (key === '9' && shift && !alt) return { kind: 'block', target: 'BLOCKQUOTE' };
  if (key === 'u' && shift && !alt) return { kind: 'link' };
  if (key === 'k' && !shift && !alt) return { kind: 'link' };
  // Slack "Apply markdown formatting" — parse typed/pasted raw markup
  // into rich formatting.
  if (key === 'f' && shift && !alt) return { kind: 'applyMarkdown' };
  return null;
}

const EMPTY_HTML = '<p><br></p>';

/**
 * Places the caret at the END OF THE JUST-PASTED CONTENT (not the end of the
 * whole editor). A marker `<span>` is dropped immediately after the last
 * inserted node so the caret target survives `normalizeBlocks` (which
 * restructures block nesting), then normalizes, restores the caret after the
 * marker, and removes it. Falls back to end-of-editor if the marker is lost.
 * Fixes pasting into the MIDDLE of an existing message throwing the caret to
 * the bottom.
 */
function placePasteCaret(
  root: HTMLElement,
  lastInserted: Node | null,
  sel: Selection,
): void {
  const doc = root.ownerDocument;
  const marker = doc.createElement('span');
  marker.setAttribute('data-ping-paste-caret', '');
  if (lastInserted !== null && lastInserted.parentNode !== null) {
    lastInserted.parentNode.insertBefore(marker, lastInserted.nextSibling);
  }
  normalizeBlocks(root);
  const placed = root.querySelector('[data-ping-paste-caret]');
  const fresh = doc.createRange();
  if (placed !== null && placed.parentNode !== null) {
    fresh.setStartAfter(placed);
    fresh.collapse(true);
    placed.parentNode.removeChild(placed);
  } else {
    // Marker lost (empty paste / unexpected normalize) → previous behaviour.
    fresh.selectNodeContents(root);
    fresh.collapse(false);
  }
  sel.removeAllRanges();
  sel.addRange(fresh);
}

const EnrichedTextInputInner = forwardRef<
  EnrichedTextInputRef,
  EnrichedTextInputProps
>(function EnrichedTextInput(props, ref) {
  const {
    defaultValue = '',
    placeholder,
    onChangeMarkdown,
    onChangeState,
    onFocus,
    onBlur,
    onSubmit,
    pressEnterToSend = true,
    disabled = false,
    onImagePaste,
    onMentionTrigger,
    onEmojiTrigger,
    onLinkEdit,
    onCodeLanguageOpen,
    onEditLast,
    convertEmoticons = true,
    testID,
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  // IME composition guard. While a CJK/other composition session is
  // active the browser owns Enter (commit candidate) and emits
  // intermediate `insertCompositionText` input events — we must NOT
  // send, run shortcuts, or auto-format until the candidate commits.
  // `compositionend` fires before the final `input`, so we clear the
  // flag there and let the trailing input emit the committed text.
  const composingRef = useRef(false);
  // The `<pre>` a code block was just quick-opened into, held only while the
  // very next keystrokes are still backticks — the window in which another
  // backtick escalates the opening fence (so ```` opens a nestable block).
  // Cleared by any non-backtick input.
  const codeFenceOpenRef = useRef<HTMLElement | null>(null);
  // Controlled undo/redo history (native contenteditable undo is unreliable
  // once we mutate the DOM for auto-format / commands / setMarkdown).
  const historyRef = useRef<EditorHistory>(new EditorHistory());
  // Set while applying an undo/redo restore so `emitChange` doesn't record the
  // restored state as a fresh history step (which would break redo).
  const suppressRecordRef = useRef(false);
  // Mirror the markdown value back into a state slot so the wrapper
  // (e.g. for `getValue()`) doesn't have to read the live DOM.
  const valueRef = useRef<string>(defaultValue);
  const [isEmpty, setIsEmpty] = useState<boolean>(defaultValue.length === 0);
  // Last emitted trigger so we can de-dup events to the host. Stored
  // by (trigger char + query length) — anchorRect changes every
  // keystroke and shouldn't gate the dedup.
  const lastTriggerKeyRef = useRef<string | null>(null);
  // The trigger window the picker is currently displaying. We hold
  // it in a ref so `insertMention` / `insertEmoji` can replace the
  // exact range even after subsequent selection moves.
  const activeTriggerRef = useRef<TriggerWindow | null>(null);
  // Last caret/selection that was inside the editor. Saved on every
  // selectionchange so that inserting an emoji/mention from a picker — which
  // steals focus (and in Safari clears the editor's selection entirely) — can
  // restore the caret and insert at the right place. Chrome keeps the selection
  // on button focus, so this only matters for Safari/Firefox, but it's safe in
  // all browsers.
  const lastRangeRef = useRef<Range | null>(null);
  // Tracks whether Shift is currently held, so the paste handler can
  // detect Cmd/Ctrl+Shift+V (paste-as-plain-text). A `ClipboardEvent`
  // carries no modifier state, so we mirror the reference composer and
  // read Shift from a global key listener at paste time.
  const shiftHeldRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent): void => {
      shiftHeldRef.current = e.shiftKey;
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
    };
  }, []);

  useLayoutEffect(() => {
    ensureEditorStyles();
    const root = rootRef.current;
    if (root === null) return;
    const html = markdownToHtml(defaultValue);
    root.innerHTML = html.length > 0 ? html : EMPTY_HTML;
    ensureCodeChrome(root);
    historyRef.current.reset(snapshotEditor(root));
    valueRef.current = defaultValue;
    // We intentionally only run this on mount — controlled remounts
    // are handled by the caller via `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const evaluateTriggers = useCallback(
    (selection: Selection | null) => {
      const root = rootRef.current;
      if (root === null) return;
      const triggered = detectTrigger(root, selection);
      if (triggered === null) {
        if (lastTriggerKeyRef.current !== null) {
          lastTriggerKeyRef.current = null;
          activeTriggerRef.current = null;
          onMentionTrigger?.(null);
          onEmojiTrigger?.(null);
        }
        return;
      }
      activeTriggerRef.current = triggered;
      const key = `${triggered.trigger}:${triggered.query}`;
      if (key === lastTriggerKeyRef.current) return;
      lastTriggerKeyRef.current = key;
      const ctx: EnrichedTriggerContext = {
        query: triggered.query,
        anchorRect: triggered.anchorRect,
      };
      if (triggered.trigger === '@') {
        onMentionTrigger?.(ctx);
        onEmojiTrigger?.(null);
      } else if (triggered.trigger === ':') {
        onEmojiTrigger?.(ctx);
        onMentionTrigger?.(null);
      }
    },
    [onMentionTrigger, onEmojiTrigger],
  );

  // The DOM→markdown serialization (`htmlToMarkdown`) is the expensive part of
  // an edit. It's coalesced to one run per animation frame; `flushSerialize`
  // runs it synchronously (used on send via `getValue`, and on unmount) so the
  // committed value is never stale. Keeps the DOM authoritative between frames.
  const serializeRafRef = useRef<number | null>(null);
  const flushSerialize = useCallback(() => {
    if (serializeRafRef.current !== null) {
      cancelAnimationFrame(serializeRafRef.current);
      serializeRafRef.current = null;
    }
    const root = rootRef.current;
    if (root === null) return;
    const markdown = htmlToMarkdown(root);
    valueRef.current = markdown;
    setIsEmpty(markdown.length === 0);
    onChangeMarkdown?.(markdown);
  }, [onChangeMarkdown]);

  const emitChange = useCallback(() => {
    const root = rootRef.current;
    if (root === null) return;
    // Keep the code-block language chips in sync with the current blocks
    // (added on paste/typing, label updated when the language changes).
    ensureCodeChrome(root);
    // Snapshot into the undo history (coalesced) unless we're mid-restore.
    if (!suppressRecordRef.current) {
      historyRef.current.record(snapshotEditor(root), Date.now());
    }
    const selection = root.ownerDocument.defaultView?.getSelection() ?? null;
    // Cheap, live parts run SYNCHRONOUSLY every keystroke: empty detection
    // (placeholder + send-enabled) from `textContent`, the toolbar active-format
    // state, and mention/emoji trigger evaluation.
    setIsEmpty((root.textContent ?? '').length === 0);
    if (onChangeState !== undefined) {
      const state: EnrichedEditorState = {
        activeFormats: deriveActiveFormats(root, selection),
        selectionEmpty: selection === null ? true : selection.isCollapsed,
        length: valueRef.current.length,
        codeLanguage: codeLanguageAt(root, selection),
      };
      onChangeState(state);
    }
    evaluateTriggers(selection);
    // Coalesce the expensive serialize to one run per frame.
    if (serializeRafRef.current === null) {
      serializeRafRef.current = requestAnimationFrame(() => {
        serializeRafRef.current = null;
        flushSerialize();
      });
    }
  }, [onChangeState, evaluateTriggers, flushSerialize]);

  // Cancel a pending serialize on unmount so it never runs against a detached
  // root (also avoids a leaked rAF).
  useEffect(() => {
    return () => {
      if (serializeRafRef.current !== null) {
        cancelAnimationFrame(serializeRafRef.current);
        serializeRafRef.current = null;
      }
    };
  }, []);

  const getSelection = useCallback((): Selection | null => {
    return rootRef.current?.ownerDocument.defaultView?.getSelection() ?? null;
  }, []);

  const runCommand = useCallback(
    (fn: (root: HTMLElement, selection: Selection | null) => void) => {
      const root = rootRef.current;
      if (root === null || disabled) return;
      const sel = getSelection();
      // If the live selection drifted out of the editor (a toolbar
      // click can collapse it on some browsers even with the
      // mousedown-preventDefault focus guard), restore the last
      // in-editor range so the command lands on the user's caret
      // instead of silently no-opping.
      const inRoot = sel?.anchorNode != null && root.contains(sel.anchorNode);
      if (
        !inRoot &&
        sel !== null &&
        lastRangeRef.current !== null &&
        root.contains(lastRangeRef.current.commonAncestorContainer)
      ) {
        sel.removeAllRanges();
        sel.addRange(lastRangeRef.current);
      }
      // Self-heal the DOM shape before every command. A prior delete-all or
      // undo can leave the root in a degenerate state — a bare text node /
      // stray `<br>` directly under the root, or an emptied block — which
      // makes `getBlockContaining` return null and turns block toggles
      // (code block, list, quote) into permanent silent no-ops until a
      // refresh rebuilds the DOM from markdown. `normalizeBlocks` re-wraps
      // stray nodes in `<p>` and guarantees a real element block at the
      // caret. It's a no-op on an already-valid tree, and it MOVES nodes
      // rather than cloning them, so the selection restored just above stays
      // anchored to the same (now-wrapped) node.
      normalizeBlocks(root);
      fn(root, sel);
      emitChange();
    },
    [disabled, emitChange, getSelection],
  );

  const insertMentionImpl = useCallback(
    (displayName: string, id?: string) => {
      const root = rootRef.current;
      if (root === null) return;
      const trigger = activeTriggerRef.current;
      const sel = getSelection();
      if (trigger === null || sel === null) return;
      commitMention(trigger.range, displayName, id, sel);
      activeTriggerRef.current = null;
      lastTriggerKeyRef.current = null;
      onMentionTrigger?.(null);
      emitChange();
    },
    [emitChange, getSelection, onMentionTrigger],
  );

  // Make sure the live selection sits inside the editor before a picker insert.
  // Safari/Firefox clear the editor's selection when a picker button takes
  // focus; restore the last saved range (or fall back to the end of the editor)
  // and re-focus so `insertNode` lands in the composer, not nowhere.
  const ensureSelectionInRoot = useCallback((): Selection | null => {
    const root = rootRef.current;
    if (root === null) return null;
    let sel = getSelection();
    const inRoot = sel?.anchorNode != null && root.contains(sel.anchorNode);
    if (inRoot) return sel;
    root.focus({ preventScroll: true });
    sel = getSelection();
    if (sel === null) return null;
    sel.removeAllRanges();
    if (lastRangeRef.current !== null && root.contains(lastRangeRef.current.commonAncestorContainer)) {
      sel.addRange(lastRangeRef.current);
    } else {
      const r = root.ownerDocument.createRange();
      r.selectNodeContents(root);
      r.collapse(false); // end of content
      sel.addRange(r);
    }
    return sel;
  }, [getSelection]);

  const insertEmojiImpl = useCallback(
    (glyph: string) => {
      const root = rootRef.current;
      if (root === null) return;
      const trigger = activeTriggerRef.current;
      const sel = trigger !== null ? getSelection() : ensureSelectionInRoot();
      if (sel === null) return;
      if (trigger !== null) {
        commitEmoji(trigger.range, glyph, sel);
        activeTriggerRef.current = null;
        lastTriggerKeyRef.current = null;
        onEmojiTrigger?.(null);
      } else if (sel.rangeCount > 0) {
        // No active trigger window — just insert at the caret.
        const range = sel.getRangeAt(0);
        const node = root.ownerDocument.createTextNode(glyph);
        range.deleteContents();
        range.insertNode(node);
        const after = root.ownerDocument.createRange();
        after.setStartAfter(node);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
      }
      emitChange();
    },
    [emitChange, flushSerialize, getSelection, ensureSelectionInRoot, onEmojiTrigger],
  );

  // Apply-markdown: re-parse the current buffer so literal markup the
  // user typed/pasted (`*bold*`, `> quote`, ``` fences, `[t](u)`) is
  // promoted to rich formatting. The round-trip is idempotent for
  // already-rich content. Caret lands at the end.
  const applyMarkdownImpl = useCallback(() => {
    const root = rootRef.current;
    if (root === null || disabled) return;
    const markdown = htmlToMarkdown(root);
    const html = markdownToHtml(markdown);
    root.innerHTML = html.length > 0 ? html : EMPTY_HTML;
    const win = root.ownerDocument.defaultView;
    const sel = win?.getSelection() ?? null;
    if (sel !== null) {
      const range = root.ownerDocument.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    emitChange();
  }, [disabled, emitChange]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => rootRef.current?.focus(),
      blur: () => rootRef.current?.blur(),
      getValue: () => {
        // Flush any frame-coalesced serialize so send always reads the latest
        // keystrokes (the DOM is authoritative between frames).
        flushSerialize();
        return valueRef.current;
      },
      setMarkdown: (markdown: string) => {
        const root = rootRef.current;
        if (root === null) return;
        // No-op when the new value matches what's already there —
        // hosts re-render on every keystroke and would otherwise
        // wipe the caret on every echo of their own state.
        if (markdown === valueRef.current) return;
        const html = markdownToHtml(markdown);
        root.innerHTML = html.length > 0 ? html : EMPTY_HTML;
        ensureCodeChrome(root);
        historyRef.current.reset(snapshotEditor(root));
        valueRef.current = markdown;
        setIsEmpty(markdown.length === 0);
        // Close any open trigger window — the caret position is no
        // longer where the trigger detector left it.
        if (activeTriggerRef.current !== null) {
          activeTriggerRef.current = null;
          lastTriggerKeyRef.current = null;
          onMentionTrigger?.(null);
          onEmojiTrigger?.(null);
        }
        // Park the caret at the end of the new content so the next
        // keystroke lands cleanly.
        const win = root.ownerDocument.defaultView;
        if (win !== null) {
          const sel = win.getSelection();
          if (sel !== null) {
            const range = root.ownerDocument.createRange();
            range.selectNodeContents(root);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      },
      toggleBold: () =>
        runCommand((root, sel) => toggleInlineMark(root, sel, 'STRONG')),
      toggleItalic: () =>
        runCommand((root, sel) => toggleInlineMark(root, sel, 'EM')),
      toggleStrikethrough: () =>
        runCommand((root, sel) => toggleInlineMark(root, sel, 'S')),
      toggleInlineCode: () =>
        runCommand((root, sel) => toggleInlineMark(root, sel, 'CODE')),
      toggleQuote: () =>
        runCommand((root, sel) => toggleBlockType(root, sel, 'BLOCKQUOTE')),
      toggleBulletList: () =>
        runCommand((root, sel) => toggleListBlock(root, sel, 'UL')),
      toggleNumberedList: () =>
        runCommand((root, sel) => toggleListBlock(root, sel, 'OL')),
      toggleCodeBlock: () =>
        runCommand((root, sel) => toggleCodeBlockCmd(root, sel)),
      setCodeBlockLanguage: (language: string) =>
        runCommand((root, sel) => {
          // The toolbar's language popover can steal the selection
          // (Safari/Firefox); fall back to the last in-editor range so
          // we still resolve the right `<pre>`.
          let pre = closestPre(root, sel?.anchorNode ?? null);
          if (pre === null && lastRangeRef.current !== null) {
            pre = closestPre(root, lastRangeRef.current.commonAncestorContainer);
          }
          if (pre === null) return;
          const lang = language.trim().toLowerCase();
          if (lang.length === 0 || lang === 'plaintext') {
            pre.removeAttribute('data-lang');
          } else {
            pre.setAttribute('data-lang', lang);
          }
        }),
      setLink: (url: string, label?: string) =>
        runCommand((root, sel) => setLinkCmd(root, sel, url, label)),
      unwrapLink: () =>
        runCommand((root, sel) => {
          unwrapLinkCmd(root, sel);
        }),
      insertMention: insertMentionImpl,
      insertEmoji: insertEmojiImpl,
      applyMarkdown: applyMarkdownImpl,
    }),
    [runCommand, insertMentionImpl, insertEmojiImpl, applyMarkdownImpl],
  );

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      const root = rootRef.current;
      if (root === null) {
        emitChange();
        return;
      }
      // Auto-format pass: inspect the character immediately before
      // the caret and rewrite a completed delimited token if any.
      // The `data` field on the native `InputEvent` carries the
      // character the user just inserted (covers keypress + IME);
      // we fall back to reading the text node ourselves so non-
      // text-insert input types (paste, drop) don't trip it.
      const native = e.nativeEvent as InputEvent;
      // Never auto-format mid-IME-composition — the intermediate
      // `insertCompositionText` events would corrupt the candidate.
      if (composingRef.current || native.isComposing) {
        emitChange();
        return;
      }
      const inserted = native.data;
      // Any non-backtick input closes the fence-escalation window.
      if (inserted !== '`') {
        codeFenceOpenRef.current = null;
      }
      if (
        inserted !== null &&
        inserted !== undefined &&
        inserted.length === 1 &&
        native.inputType !== 'insertFromPaste' &&
        native.inputType !== 'insertFromDrop'
      ) {
        const sel = getSelection();
        if (inserted === '`') {
          // A backtick typed right after the quick-open (while the block still
          // holds only a fence run) EXTENDS the opening fence — the only way to
          // reach a 4+ backtick block that can hold a nested ``` code block.
          if (
            codeFenceOpenRef.current !== null &&
            closestPre(root, sel?.anchorNode ?? null) ===
              codeFenceOpenRef.current &&
            tryEscalateCodeFenceOnType(root, sel)
          ) {
            emitChange();
            return;
          }
          // Slack-style quick-open on the 3rd backtick (no Enter needed).
          // Checked before the inline auto-format so ``` never lingers as prose.
          if (tryOpenCodeBlockOnType(root, sel)) {
            codeFenceOpenRef.current = closestPre(
              root,
              getSelection()?.anchorNode ?? null,
            );
            emitChange();
            return;
          }
          // A backtick that neither escalated nor opened a block ends the window.
          codeFenceOpenRef.current = null;
        }
        if (applyInlineAutoFormat(root, sel, inserted)) {
          emitChange();
          return;
        }
        if (convertEmoticons && applyEmoticon(root, sel, inserted)) {
          emitChange();
          return;
        }
      }
      emitChange();
    },
    [emitChange, getSelection, convertEmoticons],
  );

  const handleCompositionStart = useCallback(() => {
    composingRef.current = true;
  }, []);
  const handleCompositionEnd = useCallback(() => {
    composingRef.current = false;
    // The committed text arrives via the trailing `input` event, but
    // emit here too so state never lags a frame behind on browsers
    // that don't fire a final input after `compositionend`.
    emitChange();
  }, [emitChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const root = rootRef.current;
      if (root === null || disabled) return;
      const native = e.nativeEvent;
      // IME guard — while composing, the browser owns every key
      // (candidate navigation, commit on Enter). Don't intercept.
      if (native.isComposing || composingRef.current) return;

      // Undo / redo. We drive our own history because native contenteditable
      // undo desyncs once we mutate the DOM (auto-format, commands,
      // setMarkdown). Cmd/Ctrl+Z = undo; +Shift or Cmd/Ctrl+Y = redo.
      {
        const mod = native.metaKey || native.ctrlKey;
        const lower = native.key.toLowerCase();
        if (
          mod &&
          !native.altKey &&
          (lower === 'z' || (lower === 'y' && !native.shiftKey))
        ) {
          e.preventDefault();
          const snap =
            lower === 'y' || native.shiftKey
              ? historyRef.current.redo()
              : historyRef.current.undo();
          if (snap !== null) {
            root.innerHTML = snap.html.length > 0 ? snap.html : EMPTY_HTML;
            // A restored snapshot can carry a degenerate shape (stray nodes
            // under the root, an emptied block). Normalize before re-chroming
            // and restoring the caret so block toggles don't get stuck after
            // an undo — `restoreCaretOffset` is character-offset based, so it
            // survives the re-wrapping.
            normalizeBlocks(root);
            ensureCodeChrome(root);
            restoreCaretOffset(root, snap.caret);
            suppressRecordRef.current = true;
            try {
              emitChange();
            } finally {
              suppressRecordRef.current = false;
            }
          }
          return;
        }
      }

      // ↑ in an empty editor → edit last message (Slack). Only when no
      // modifier is held and no trigger window is open, so it never
      // steals arrow navigation inside a picker or a non-empty buffer.
      if (
        native.key === 'ArrowUp' &&
        onEditLast !== undefined &&
        !native.shiftKey &&
        !native.metaKey &&
        !native.ctrlKey &&
        !native.altKey &&
        valueRef.current.length === 0 &&
        activeTriggerRef.current === null
      ) {
        e.preventDefault();
        onEditLast();
        return;
      }
      // ↓ on the last line of a TRAILING code block (nothing after it) escapes
      // to a fresh line below, so a code block at the end never traps the
      // caret. When a block already follows, the browser's own ↓ moves into
      // it — no interception needed.
      if (
        native.key === 'ArrowDown' &&
        !native.shiftKey &&
        !native.metaKey &&
        !native.ctrlKey &&
        !native.altKey &&
        activeTriggerRef.current === null
      ) {
        const sel = getSelection();
        const pre = closestPre(root, sel?.anchorNode ?? null);
        if (
          pre !== null &&
          pre.nextElementSibling === null &&
          isCaretOnLastLineOfCode(root, sel) &&
          tryExitCodeBlockBelow(root, sel)
        ) {
          e.preventDefault();
          emitChange();
          return;
        }
      }
      const shortcut = resolveShortcut(native);
      if (shortcut !== null) {
        e.preventDefault();
        // Keyboard shortcuts run the block/inline commands directly (not
        // through `runCommand`), so heal a degenerate DOM here too —
        // otherwise the code-block shortcut stays a no-op after a
        // delete-all / undo, matching the toolbar path's guard.
        normalizeBlocks(root);
        const sel = getSelection();
        switch (shortcut.kind) {
          case 'inline':
            toggleInlineMark(root, sel, shortcut.tag);
            break;
          case 'block':
            toggleBlockType(root, sel, shortcut.target);
            break;
          case 'list':
            toggleListBlock(root, sel, shortcut.tag);
            break;
          case 'codeBlock':
            toggleCodeBlockCmd(root, sel);
            break;
          case 'link':
            setLinkCmd(root, sel, 'https://');
            break;
          case 'applyMarkdown':
            applyMarkdownImpl();
            return;
        }
        emitChange();
        return;
      }
      // Tab inside a list — indent / outdent. Outside lists Tab falls
      // through (browser default: focus next element).
      if (native.key === 'Tab') {
        const sel = getSelection();
        const ok = native.shiftKey
          ? outdentListItem(root, sel)
          : indentListItem(root, sel);
        if (ok) {
          e.preventDefault();
          emitChange();
        }
        return;
      }
      // Enter handling — code block, list, fence-open, then submit.
      if (native.key === 'Enter') {
        const sel = getSelection();
        const inCodeBlock =
          sel !== null &&
          sel.anchorNode !== null &&
          climbHasTag(root, sel.anchorNode, 'PRE');
        if (inCodeBlock) {
          // Plain Enter on a closing ``` fence line exits the block.
          if (!native.shiftKey && tryExitCodeFence(root, sel)) {
            e.preventDefault();
            emitChange();
            return;
          }
          // Shift+Enter on the LAST line escapes the block to a new line
          // below (paired with ↓). Elsewhere Shift+Enter just inserts a
          // newline. We never send from inside a code block — the user
          // exits first, then Enter sends.
          if (
            native.shiftKey &&
            isCaretOnLastLineOfCode(root, sel) &&
            tryExitCodeBlockBelow(root, sel)
          ) {
            e.preventDefault();
            emitChange();
            return;
          }
          // Otherwise: browser default newline inside the block.
          return;
        }
        // Outside a code block: `` ``` ``+Shift+Enter opens a fenced block
        // (fallback to the third-backtick input rule), empty <li> exits a
        // list, else the `pressEnterToSend` policy applies.
        if (native.shiftKey && tryConvertParagraphToCodeBlock(root, sel)) {
          e.preventDefault();
          emitChange();
          return;
        }
        if (handleEnterInList(root, sel)) {
          e.preventDefault();
          emitChange();
          return;
        }
        if (onSubmit !== undefined && pressEnterToSend && !native.shiftKey) {
          e.preventDefault();
          onSubmit();
        }
        return;
      }
      // Backspace inside an empty code block — remove it cleanly before the
      // browser can leave a chip-only `<pre>` zombie (which jams the
      // code-block toggle until reload).
      if (
        native.key === 'Backspace' &&
        !native.metaKey &&
        !native.ctrlKey &&
        !native.altKey &&
        !native.shiftKey
      ) {
        const sel = getSelection();
        if (handleBackspaceInCodeBlock(root, sel)) {
          e.preventDefault();
          emitChange();
          return;
        }
      }
      // Backspace at list start — outdent / convert to <p>.
      if (native.key === 'Backspace') {
        const sel = getSelection();
        if (handleBackspaceInList(root, sel)) {
          e.preventDefault();
          emitChange();
          return;
        }
      }
      // Escape — close the active trigger window if any.
      if (native.key === 'Escape' && activeTriggerRef.current !== null) {
        activeTriggerRef.current = null;
        lastTriggerKeyRef.current = null;
        onMentionTrigger?.(null);
        onEmojiTrigger?.(null);
        e.preventDefault();
      }
    },
    [
      disabled,
      emitChange,
      getSelection,
      onSubmit,
      pressEnterToSend,
      onMentionTrigger,
      onEmojiTrigger,
      onEditLast,
      applyMarkdownImpl,
    ],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const root = rootRef.current;
      if (root === null || disabled) return;
      e.preventDefault();
      const sel = getSelection();
      if (sel === null || sel.rangeCount === 0) return;
      const classified = classifyClipboard(e.clipboardData);

      // Image takes precedence. Hand the file off to the host and
      // skip text insertion entirely — the host shows a preview chip
      // / attachment pill outside the editor.
      if (classified.image !== null) {
        onImagePaste?.(classified.image);
        return;
      }

      const range = sel.getRangeAt(0);

      // Paste-as-plain-text (Cmd/Ctrl+Shift+V). Strip every Markdown
      // marker from the clipboard text and insert it verbatim — no code
      // detection, no autolink-to-rich, no HTML preservation. This is
      // the escape hatch for "I want the characters, not the
      // formatting", matching the reference composer's plain-paste path.
      if (shiftHeldRef.current && classified.text.length > 0) {
        range.deleteContents();
        const stripped = stripMarkdownFormatting(classified.text);
        const frag = plainTextToFragment(stripped, root.ownerDocument);
        const last = frag.lastChild;
        range.insertNode(frag);
        placePasteCaret(root, last, sel);
        emitChange();
        return;
      }

      // URL on a non-empty selection → wrap selection as a link.
      if (classified.textIsUrl && !range.collapsed) {
        const doc = root.ownerDocument;
        const a = doc.createElement('a');
        a.setAttribute('href', normalizeUrl(classified.text));
        a.appendChild(range.extractContents());
        range.insertNode(a);
        const after = doc.createRange();
        after.setStartAfter(a);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
        emitChange();
        return;
      }

      range.deleteContents();

      // ── Multi-line paste fidelity ─────────────────────────────────
      // Two failure modes Slack handles that a naive HTML paste does
      // not:
      //   1. Code copied from an IDE/rendered view encodes its line
      //      breaks as block LAYOUT of inline spans. Once sanitized
      //      those spans collapse and EVERY newline is lost — the
      //      composer then sees one giant line (round-pill chrome
      //      stretches into an oval) and the tile renders a wall of
      //      text. The `text/plain` payload always has the real `\n`s.
      //   2. A pasted code snippet should become a code block.
      // We branch on the plain-text payload's line structure BEFORE
      // trusting the (possibly collapsed) HTML.
      const plainText = classified.text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const plainLineCount = plainText.length > 0 ? plainText.split('\n').length : 0;
      // Blank lines between paragraphs inflate the raw line count without
      // adding block structure — a two-paragraph rich paste is
      // `<p>…</p><p>…</p>` (2 blocks) but "para1\n\npara2" (3 lines). Compare
      // the HTML's block/`<br>` count against the count of NON-BLANK lines so
      // ordinary multi-paragraph rich text is NOT mistaken for a payload whose
      // newlines collapsed — otherwise it falls to the plain-text rebuild
      // below and loses all its bold/italic/link formatting.
      const plainNonBlankLineCount =
        plainText.length > 0
          ? plainText.split('\n').filter((l) => l.trim().length > 0).length
          : 0;
      const htmlHasPre = /<pre[\s>]/i.test(classified.html);
      // "Collapsed" == the HTML genuinely lost line structure (an IDE/table
      // copy whose newlines were inline-span LAYOUT, not `<p>`/`<br>`). Only
      // then do we throw away the HTML and rebuild from plain text.
      const htmlCollapsedNewlines =
        plainNonBlankLineCount > 1 &&
        htmlStructuralLineCount(classified.html) < plainNonBlankLineCount;

      // Pasted markdown containing ``` fences → parse the WHOLE payload
      // so each fenced block becomes its own code block (with its own
      // auto-detected language) interleaved with the surrounding text.
      // This is how multiple code blocks land in-between content.
      if (plainText.length > 0 && /(^|\n)```/.test(plainText)) {
        const tpl = root.ownerDocument.createElement('template');
        tpl.innerHTML = markdownToHtml(plainText);
        autodetectCodeBlocks(tpl.content);
        autolinkInPlace(tpl.content);
        const last = tpl.content.lastChild;
        range.insertNode(tpl.content);
        placePasteCaret(root, last, sel);
        emitChange();
        return;
      }

      // Code snippet → fenced code block (verbatim whitespace).
      if (
        plainText.length > 0 &&
        (htmlHasPre || (plainLineCount > 1 && looksLikeCode(plainText)))
      ) {
        const frag = buildCodeBlockFragment(plainText, root.ownerDocument);
        const last = frag.lastChild;
        range.insertNode(frag);
        placePasteCaret(root, last, sel);
        emitChange();
        return;
      }

      // Multi-line non-code paste whose HTML lost the line breaks →
      // rebuild from plain text so every newline survives.
      if (plainText.length > 0 && (classified.html.length === 0 || htmlCollapsedNewlines)) {
        const frag = plainTextToFragment(plainText, root.ownerDocument);
        const last = frag.lastChild;
        range.insertNode(frag);
        placePasteCaret(root, last, sel);
        emitChange();
        return;
      }

      // Prefer cleaned HTML when present — preserves bold/italic/
      // links from rich pastes. Run an autolink sweep so plain URLs
      // inside paragraphs become clickable links.
      let lastInserted: Node | null = null;
      if (classified.html.length > 0) {
        const tpl = root.ownerDocument.createElement('template');
        tpl.innerHTML = classified.html;
        autolinkInPlace(tpl.content);
        lastInserted = tpl.content.lastChild;
        range.insertNode(tpl.content);
      } else if (classified.text.length > 0) {
        if (classified.textIsUrl) {
          const doc = root.ownerDocument;
          const a = doc.createElement('a');
          a.setAttribute('href', normalizeUrl(classified.text));
          a.textContent = classified.text;
          range.insertNode(a);
          lastInserted = a;
        } else {
          const frag = plainTextToFragment(classified.text, root.ownerDocument);
          lastInserted = frag.lastChild;
          range.insertNode(frag);
        }
      }
      // Repair block-level nesting that the browser produced when
      // inserting a fragment of block elements into the current
      // paragraph's range. Without this, `<p>existing<p>line1</p>
      // <p>line2</p></p>` would collapse to one inline run on
      // serialization, dropping the newlines — and the composer's
      // `hasNewLine` flag would stay false, breaking the pill-vs-
      // rounded-rect chrome on multi-line paste. Caret lands at the end of
      // the INSERTED content, not the whole editor.
      placePasteCaret(root, lastInserted, sel);
      emitChange();
    },
    [disabled, emitChange, getSelection, onImagePaste],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const root = rootRef.current;
      if (root === null || disabled) return;
      const files = e.dataTransfer?.files;
      if (files === undefined || files === null || files.length === 0) return;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f !== undefined && f.type.startsWith('image/')) {
          e.preventDefault();
          onImagePaste?.(f);
          return;
        }
      }
    },
    [disabled, onImagePaste],
  );

  // Intercept clicks on links — default browser behavior would
  // navigate to the href, which is wrong inside an editor. We
  // preventDefault, park the caret inside the anchor, and surface
  // the link to the host so it can show an edit popover.
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const root = rootRef.current;
      if (root === null) return;
      const clickTarget = e.target as HTMLElement | null;

      // Code-block language chip → open the language picker. The chip is
      // contenteditable=false, so we manually park the caret inside its
      // <pre> so `setCodeBlockLanguage` targets this block.
      const chip = clickTarget?.closest?.(`[${CODE_LANG_CHIP_ATTR}]`) as
        | HTMLElement
        | null
        | undefined;
      if (chip != null && root.contains(chip)) {
        e.preventDefault();
        const pre = chip.closest('pre');
        if (pre !== null) {
          const code = pre.querySelector('code') ?? pre;
          const sel = getSelection();
          if (sel !== null) {
            const range = root.ownerDocument.createRange();
            range.selectNodeContents(code);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }
          if (onCodeLanguageOpen !== undefined) {
            let rect: DOMRect | null = null;
            try {
              rect = chip.getBoundingClientRect();
            } catch {
              rect = null;
            }
            onCodeLanguageOpen({
              language: (pre.getAttribute('data-lang') ?? '').trim(),
              anchorRect: rect,
            });
          }
        }
        return;
      }

      // Only Shift+Click opens the link editor. A plain click just places
      // the caret (contenteditable anchors don't navigate on click), so
      // the user can edit link text normally without a popover popping up.
      if (!e.shiftKey) return;
      const target = e.target as Node | null;
      if (target === null) return;
      const anchor = findAncestorAnchor(root, target);
      if (anchor === null) return;
      e.preventDefault();
      // Park the caret inside the anchor so subsequent edits (e.g.
      // `setLink(newUrl)` from the host popover) operate on this
      // link rather than reaching for a stale selection.
      const sel = getSelection();
      if (sel !== null) {
        const range = root.ownerDocument.createRange();
        range.selectNodeContents(anchor);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      if (onLinkEdit !== undefined) {
        let rect: DOMRect | null = null;
        try {
          rect = anchor.getBoundingClientRect();
        } catch {
          rect = null;
        }
        onLinkEdit({
          url: anchor.getAttribute('href') ?? '',
          label: anchor.textContent ?? '',
          anchorRect: rect,
        });
      }
    },
    [getSelection, onLinkEdit, onCodeLanguageOpen],
  );

  const handleFocus = useCallback(() => onFocus?.(), [onFocus]);
  const handleBlur = useCallback(() => {
    // Closing the editor closes the trigger window too — the host
    // dismisses its picker on blur.
    if (activeTriggerRef.current !== null) {
      activeTriggerRef.current = null;
      lastTriggerKeyRef.current = null;
      onMentionTrigger?.(null);
      onEmojiTrigger?.(null);
    }
    onBlur?.();
  }, [onBlur, onMentionTrigger, onEmojiTrigger]);

  // Selection change handler — emits state-only updates (not text)
  // so the toolbar's active-format chips track the caret. Also
  // re-evaluates the mention/emoji trigger window after caret moves.
  useEffect(() => {
    const root = rootRef.current;
    if (root === null) return;
    const doc = root.ownerDocument;
    const handle = () => {
      // Only react to selection changes that fall inside our root —
      // selectionchange fires globally.
      const sel = doc.defaultView?.getSelection() ?? null;
      if (sel === null || sel.anchorNode === null) return;
      if (!root.contains(sel.anchorNode)) return;
      // Remember the in-editor caret so picker insertions can restore it.
      if (sel.rangeCount > 0) lastRangeRef.current = sel.getRangeAt(0).cloneRange();
      if (onChangeState !== undefined) {
        onChangeState({
          activeFormats: deriveActiveFormats(root, sel),
          selectionEmpty: sel.isCollapsed,
          length: valueRef.current.length,
          codeLanguage: codeLanguageAt(root, sel),
        });
      }
      evaluateTriggers(sel);
    };
    doc.addEventListener('selectionchange', handle);
    return () => doc.removeEventListener('selectionchange', handle);
  }, [onChangeState, evaluateTriggers]);

  return (
    <div
      ref={rootRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={placeholder ?? 'Message'}
      data-testid={testID}
      data-placeholder={isEmpty ? placeholder ?? '' : undefined}
      className="enriched-text-input"
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onPaste={handlePaste}
      onDrop={handleDrop}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{
        outline: 'none',
        whiteSpace: 'pre-wrap',
        // `break-word` (not `break-all`) so normal prose wraps at word
        // boundaries; a single very long token (URL / code) still wraps
        // via `overflow-wrap` instead of overflowing the pill.
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minHeight: 22,
        // Cap growth (~9 lines, Slack-style) then scroll INTERNALLY so a
        // huge paste can never stretch the composer pill past the cap or
        // distort its rounded border.
        maxHeight: 200,
        overflowY: 'auto',
      }}
    />
  );
});

/** Closest `<pre>` ancestor of `node` within `root`, else null. */
function closestPre(root: HTMLElement, node: Node | null): HTMLElement | null {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (cur.nodeType === Node.ELEMENT_NODE && (cur as HTMLElement).tagName === 'PRE') {
      return cur as HTMLElement;
    }
    cur = cur.parentNode;
  }
  return null;
}

/**
 * Language label for a caret inside a code block: explicit `data-lang`
 * wins, else the auto-detected language, else `''`. `null` when the
 * caret is not inside a code block.
 */
function codeLanguageAt(root: HTMLElement, selection: Selection | null): string | null {
  if (selection === null || selection.rangeCount === 0) return null;
  const pre = closestPre(root, selection.anchorNode);
  if (pre === null) return null;
  const explicit = (pre.getAttribute('data-lang') ?? '').trim();
  if (explicit.length > 0) return explicit;
  const code = pre.querySelector('code');
  return detectLanguage(code?.textContent ?? pre.textContent ?? '') ?? '';
}

function climbHasTag(
  root: HTMLElement,
  node: Node,
  tag: string,
): boolean {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (cur.nodeType === Node.ELEMENT_NODE && (cur as HTMLElement).tagName === tag) {
      return true;
    }
    cur = cur.parentNode;
  }
  return false;
}

export const EnrichedTextInput = React.memo(EnrichedTextInputInner);
